import React from "react";
import { Link } from "react-router-dom";
import "./LandingDisplay.css";
import { HDMI_PRODUCTS, HDMI_FAQ } from "./HdmiDisplayPage";

/**
 * /hdmi-display/raspberry-pi-setup -- how-to guide for connecting the BIBA HDMI
 * touch displays to a Raspberry Pi. Static, prerendered (prerender.js
 * STATIC_ROUTES; <head> tags and HowTo/FAQ schema in src/seo/pageMeta.js).
 *
 * Every statement here comes from the /hdmi-display landing page (HDMI_FAQ,
 * setup steps, spec rows). Do not add facts that are not on that page.
 */

// Cable by Raspberry Pi model, from the landing page FAQ.
export const PI_CABLES = [
  ["Raspberry Pi 5", "Micro HDMI to HDMI", "Any USB-A port"],
  ["Raspberry Pi 4", "Micro HDMI to HDMI", "Any USB-A port"],
  ["Raspberry Pi 3", "Full-size HDMI", "Any USB-A port"],
  ["Raspberry Pi Zero 2 W", "Mini HDMI to HDMI", "Any USB port"],
  ["Compute Module 4 carrier boards", "Full-size HDMI on most carriers", "Any USB port"],
];

export const PI_STEPS = [
  {
    name: "Connect the HDMI cable",
    text: "Plug the HDMI cable from the Raspberry Pi into the display. Use micro HDMI to HDMI on a Pi 4 or Pi 5, full-size HDMI on a Pi 3, and mini HDMI on a Zero 2 W. The display reports its native 1024 x 600 mode over EDID.",
  },
  {
    name: "Connect the USB cable",
    text: "Plug the USB cable from the display into any USB port on the Pi. This one cable carries 5 V power and the capacitive touch data, and the touch controller shows up as a standard USB HID device.",
  },
  {
    name: "Boot Raspberry Pi OS",
    text: "Power on the Pi. Raspberry Pi OS detects the 1024 x 600 resolution and the touch screen automatically. No config.txt changes and no driver install are needed.",
  },
  {
    name: "Rotate the screen if you mount it in portrait",
    text: "On Raspberry Pi OS Bookworm open Preferences, Screen Configuration, select the HDMI output and set the orientation. Touch follows the rotation automatically under Wayland.",
  },
];

// FAQ entries from the landing page that this guide answers; the page and its
// FAQPage schema reuse them verbatim so the two cannot drift apart.
const GUIDE_FAQ_QUESTIONS = [
  "Does the 7 inch HDMI display work with a Raspberry Pi 5?",
  "Does it work with Raspberry Pi 4, Pi 3, Zero 2 W and Compute Module 4?",
  "Do I need a driver for the touch screen?",
  "How is the display powered?",
  "How do I rotate the screen to portrait?",
];
export const PI_GUIDE_FAQ = HDMI_FAQ.filter((f) => GUIDE_FAQ_QUESTIONS.includes(f.q));

export default function HdmiRaspberryPiGuidePage() {
  return (
    <div className="ld-root">
      <section className="ld-hero">
        <div className="ld-wrap ld-hero-grid">
          <div>
            <span className="ld-eyebrow">Setup guide</span>
            <h1 className="ld-h1">
              How to connect a <em>7 inch HDMI touch display</em> to a Raspberry Pi
            </h1>
            <p className="ld-lead">
              Two cables, no drivers and no config.txt changes. This guide covers the cable for each Raspberry Pi
              model, powering the display, touch, and rotating the screen to portrait.
            </p>
            <div className="ld-cta-row">
              <Link to={HDMI_PRODUCTS[0].route} className="ld-btn ld-btn-primary">
                Buy the 7 inch display
              </Link>
              <Link to="/hdmi-display" className="ld-btn ld-btn-secondary">
                See both sizes
              </Link>
            </div>
          </div>
          <div className="ld-hero-media">
            <img
              src="/landing/compat/raspberry-pi.jpg"
              alt="BIBA 7 inch HDMI touch display connected to a Raspberry Pi class single-board computer with HDMI and USB cables"
              width="800"
              height="600"
              fetchPriority="high"
            />
            <p className="ld-hero-caption">The 7 inch display wired to a Raspberry Pi with one HDMI and one USB cable</p>
          </div>
        </div>
      </section>

      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">What you need</h2>
          </div>
          <div className="ld-prose">
            <ul>
              <li>
                A BIBA HDMI touch display: the{" "}
                <Link to={HDMI_PRODUCTS[0].route}>7 inch, part {HDMI_PRODUCTS[0].pn}</Link> or the{" "}
                <Link to={HDMI_PRODUCTS[1].route}>10.1 inch, part {HDMI_PRODUCTS[1].pn}</Link>. Both are 1024 x 600
                pixels.
              </li>
              <li>A Raspberry Pi with an HDMI output, running Raspberry Pi OS.</li>
              <li>The HDMI cable that fits your Pi model (table below).</li>
              <li>A USB cable from the display to a USB port on the Pi, for 5 V power and touch.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Setup in four steps</h2>
            <p className="ld-sub">The same steps apply to a Raspberry Pi 5, 4, 3, Zero 2 W or Compute Module 4.</p>
          </div>
          <div className="ld-steps">
            {PI_STEPS.map((s, i) => (
              <div className="ld-step" key={s.name} id={`step-${i + 1}`}>
                <span className="ld-step-num">{i + 1}</span>
                <h3>{s.name}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Which cable for which Raspberry Pi</h2>
            <p className="ld-sub">Only the HDMI cable changes between models. Touch and power always use the USB cable.</p>
          </div>
          <div className="ld-table-wrap">
            <table className="ld-table">
              <thead>
                <tr>
                  <th>Raspberry Pi</th>
                  <th>HDMI cable</th>
                  <th>USB for touch and power</th>
                </tr>
              </thead>
              <tbody>
                {PI_CABLES.map(([pi, hdmi, usb]) => (
                  <tr key={pi}>
                    <th scope="row">{pi}</th>
                    <td>{hdmi}</td>
                    <td>{usb}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="ld-section">
        <div className="ld-wrap ld-two-col">
          <div className="ld-prose">
            <h2 className="ld-h2">Powering the display</h2>
            <p>
              The display is powered at 5 V through its USB connector. A Raspberry Pi USB port is enough for the
              7 inch model. For the 10.1 inch model we recommend a dedicated 5 V, 2 A supply so the backlight does
              not share the Pi's USB budget.
            </p>
            <h2 className="ld-h2" style={{ marginTop: 28 }}>Touch without drivers</h2>
            <p>
              The capacitive touch controller is a standard USB HID device. Raspberry Pi OS recognises it without
              installing anything, and the same is true of Ubuntu, Debian, Windows 10 and 11, and Android.
            </p>
          </div>
          <aside className="ld-aside">
            <h3>At a glance</h3>
            <ul>
              <li>Resolution: 1024 x 600 pixels</li>
              <li>Video: HDMI, EDID auto-detect</li>
              <li>Touch: capacitive, USB HID</li>
              <li>Power: 5 V over USB</li>
              <li>Brightness: 500 nits</li>
              <li>Operating range: -20 to 70 C</li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="ld-section ld-section-alt">
        <div className="ld-wrap">
          <div className="ld-section-head">
            <h2 className="ld-h2">Raspberry Pi HDMI touch display questions</h2>
          </div>
          <div className="ld-faq">
            {PI_GUIDE_FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
          <div className="ld-crosslink">
            Using a Jetson, a Windows PC or Ubuntu instead? See the{" "}
            <Link to="/hdmi-display/jetson-windows-ubuntu-setup">Jetson, Windows and Ubuntu setup guide</Link>, or the{" "}
            <Link to="/hdmi-display">HDMI display page</Link> for every supported host. Driving the screen from a
            microcontroller such as an Arduino, ESP32 or STM32? See the <Link to="/uart-display">Smart UART displays</Link>.
          </div>
        </div>
      </section>

      <section className="ld-section">
        <div className="ld-wrap">
          <div className="ld-cta">
            <div>
              <h2>Order online or talk to an engineer</h2>
              <p>
                Single units ship from stock in Gurugram. For 100 pieces and above, or a custom cover glass, cable or
                enclosure, send us your requirement and we reply within one working day.
              </p>
            </div>
            <div className="ld-cta-actions">
              <Link to={HDMI_PRODUCTS[0].route} className="ld-btn ld-btn-primary">Buy 7 inch</Link>
              <Link to={HDMI_PRODUCTS[1].route} className="ld-btn ld-btn-primary">Buy 10.1 inch</Link>
              <Link to="/contact" className="ld-btn ld-btn-secondary">Request a quote</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
